import {
  AgGridMeter,
  EllipsisBoxWithTooltip,
  FormControlSizes,
  HStack,
  Icon,
  IconButton,
  IconName,
  LPSettlementBatchStatusEnum,
  Tooltip,
  toBigWithDefault,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import type { IRowNode } from 'ag-grid-enterprise';
import { memo } from 'react';
import { ManualSettlementBadge } from '../BatchDetails/components/ManualSettlementBadge';
import type { useSettleBatchContext } from '../SettleBatchContext';
import { LPBatchStatusRenderer } from '../components/LPBatchStatusRenderer';
import { LPInstructionStatusRenderer } from '../components/LPInstructionStatusRenderer';
import type { LPSettlementBatchMessageData } from '../generated/lpSettlementTypes';
import { getCreatedByColumnDisplayValue } from '../utils/lpBatchUtils';
import type { LPSettlementInstructionRow } from './LPSettlementBatchesGrid';

// Helper functions

type BatchDataResult = {
  data: LPSettlementInstructionRow | null;
  shouldDisplay: boolean;
};

/**
 * Gets batch-level data from a group row by accessing the first child's data.
 * Returns an object with the data and whether it should be displayed.
 */
function getBatchLevelData(node: IRowNode<LPSettlementInstructionRow>): BatchDataResult {
  if (node.group && node.field === 'SettleBatchID') {
    return {
      data: node.allLeafChildren?.[0]?.data ?? null,
      shouldDisplay: true,
    };
  }
  return {
    data: null,
    shouldDisplay: false,
  };
}

/**
 * Gets batch-level data for display in either the batch column or auto-group column.
 * Works for both batch ID group rows and any group level when showing in auto-group column.
 *
 * @returns An object with:
 *   - data: The batch data from the first child, or null if not available
 *   - shouldDisplay: Whether the value should be displayed in this location
 */
function getBatchDataForDisplay(node: IRowNode<LPSettlementInstructionRow>, colDef?: ColDef): BatchDataResult {
  // First check if it's a batch ID group level
  const batchLevelResult = getBatchLevelData(node);
  if (batchLevelResult.shouldDisplay) {
    return batchLevelResult;
  }

  // Also check if it's any group level in the auto-group column
  if (node.group && colDef?.showRowGroup) {
    return {
      data: node.allLeafChildren?.[0]?.data ?? null,
      shouldDisplay: true,
    };
  }

  // Not a location where we should display batch data
  return {
    data: null,
    shouldDisplay: false,
  };
}

// Cell renderer components

export const BatchIDCell = memo(function BatchIDCell(props: ICellRendererParams<LPSettlementInstructionRow>) {
  const { node, data } = props;

  // Get batch data: use helper for group rows, otherwise use current data
  const batchData = node.group && node.field === 'SettleBatchID' ? getBatchLevelData(node).data : data;
  if (!batchData) {
    return null;
  }

  const batchId = batchData?.SettleBatchID;
  const isLocked = batchData.BatchStatus !== LPSettlementBatchStatusEnum.Created;

  return (
    <HStack gap="spacing4" alignItems="center">
      <span>Batch: {batchId}</span>
      <ManualSettlementBadge creationSource={batchData.BatchCreationSource} />
      <Tooltip tooltip={isLocked ? 'Locked' : 'Unlocked'}>
        <Icon icon={isLocked ? IconName.LockClosed : IconName.LockOpen} size={12} />
      </Tooltip>
    </HStack>
  );
});

export const SettlementSubAccountsCell = memo(function SettlementSubAccountsCell(
  params: ICellRendererParams<LPSettlementInstructionRow>
) {
  const { node, colDef, context, value } = params;

  // Check if we should display at group level (SettleBatch group or auto-group column)
  const { data: batchData, shouldDisplay } = getBatchDataForDisplay(node, colDef);

  if (!shouldDisplay) {
    return null;
  }

  if (batchData?.BatchSettlementSubAccounts) {
    // Format sub-accounts for display at group level
    const formattedSubAccounts =
      batchData.BatchSettlementSubAccounts.map(
        (account: { SubAccount: string }) =>
          context.current?.subAccountsByName?.get(account.SubAccount)?.DisplayName ?? account.SubAccount
      )
        .toSorted()
        .join(', ') || '';

    return (
      <EllipsisBoxWithTooltip tooltipProps={{ placement: 'bottom-start' }}>
        {formattedSubAccounts}
      </EllipsisBoxWithTooltip>
    );
  }

  // For leaf rows, display the value as-is (already formatted by valueGetter)
  return <EllipsisBoxWithTooltip tooltipProps={{ placement: 'bottom-start' }}>{value}</EllipsisBoxWithTooltip>;
});

export const BatchReportCountCell = memo(function BatchReportCountCell(
  props: ICellRendererParams<LPSettlementInstructionRow>
) {
  const { node } = props;

  // Only show at Batch ID group level (not in auto-group column)
  const { data: batchData, shouldDisplay } = getBatchLevelData(node);
  if (!shouldDisplay || !batchData?.BatchReportCount) {
    return null;
  }

  return (
    <EllipsisBoxWithTooltip tooltipProps={{ placement: 'bottom-start' }}>
      {batchData.BatchReportCount}
    </EllipsisBoxWithTooltip>
  );
});

export const BatchCreationUserCell = memo(function BatchCreationUserCell(
  props: ICellRendererParams<LPSettlementInstructionRow>
) {
  const { node, colDef } = props;

  const { data: batchData, shouldDisplay } = getBatchDataForDisplay(node, colDef);

  if (!shouldDisplay) {
    return null;
  }

  return (
    <EllipsisBoxWithTooltip tooltipProps={{ placement: 'bottom-start' }}>
      {getCreatedByColumnDisplayValue(batchData)}
    </EllipsisBoxWithTooltip>
  );
});

export const BatchCounterpartyCell = memo(function BatchCounterpartyCell(
  props: ICellRendererParams<LPSettlementInstructionRow>
) {
  const { node, colDef, context } = props;

  const { data: batchData, shouldDisplay } = getBatchDataForDisplay(node, colDef);

  if (!shouldDisplay) {
    return null;
  }

  // Resolve display name from context
  const displayName = batchData?.BatchCounterparty
    ? (context.current?.counterpartiesByName?.get(batchData.BatchCounterparty)?.DisplayName ??
      batchData.BatchCounterparty)
    : undefined;

  const counterpartyForDisplay = displayName ?? '(Unknown)';

  return (
    <EllipsisBoxWithTooltip tooltipProps={{ placement: 'bottom-start' }}>
      {counterpartyForDisplay}
    </EllipsisBoxWithTooltip>
  );
});

export const CombinedStatusCell = memo(function CombinedStatusCell(
  props: ICellRendererParams<LPSettlementInstructionRow>
) {
  const { node, context, data } = props;

  if (!context.current.theme) {
    return null;
  }

  // For group rows at the BatchID level, show BatchStatus
  const { data: batchData, shouldDisplay } = getBatchLevelData(node);
  if (shouldDisplay && batchData?.BatchStatus) {
    return <LPBatchStatusRenderer theme={context.current.theme} status={batchData.BatchStatus} />;
  }

  // For leaf rows (instructions), show InstructionStatus
  if (!node.group && data?.Status) {
    return (
      <LPInstructionStatusRenderer
        theme={context.current.theme}
        status={data.Status}
        direction={data.Direction}
        errorText={data.ErrorText || null}
      />
    );
  }

  return null;
});

export const BatchProgressCell = memo(function BatchProgressCell(
  props: ICellRendererParams<LPSettlementInstructionRow>
) {
  const { node, colDef } = props;

  const { data: batchData, shouldDisplay } = getBatchDataForDisplay(node, colDef);

  if (!shouldDisplay || !batchData) {
    return null;
  }

  // Create a batch object with the required fields for BatchProgress
  const settlementBatch: LPSettlementBatchMessageData = {
    PercentCompleted: batchData.BatchPercentCompleted ?? '0',
  } as LPSettlementBatchMessageData;

  return (
    <AgGridMeter<LPSettlementInstructionRow>
      {...props}
      value={toBigWithDefault(settlementBatch.PercentCompleted, 0).div(100).toString()}
      showInitialAnimation={false}
      showLabel
    />
  );
});

interface BatchDetailsButtonCellProps extends ICellRendererParams<LPSettlementInstructionRow> {
  openBatchDetails: ReturnType<typeof useSettleBatchContext>['openBatchDetails'];
}

export const BatchDetailsButtonCell = memo(function BatchDetailsButtonCell(props: BatchDetailsButtonCellProps) {
  const { node, openBatchDetails } = props;

  // Get batch ID: from batch data for group rows, from node.data for leaf rows
  let batchId: string | undefined;

  if (node.group && node.field === 'SettleBatchID') {
    // Batch-level group row - get batch ID from first child
    const batchData = getBatchLevelData(node).data;
    batchId = batchData?.SettleBatchID;
  }

  if (!batchId) {
    return null;
  }

  return (
    <IconButton
      icon={IconName.Deepdive}
      size={FormControlSizes.Small}
      onClick={() => {
        openBatchDetails(batchId);
      }}
    />
  );
});
